import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Users, Zap } from 'lucide-react';
import QuizParticipant from '../components/QuizParticipant';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuizJoinPage() {
  const { code: urlCode } = useParams();
  const [joinCode, setJoinCode] = useState(urlCode || '');
  const [name, setName] = useState('');
  const [teamId, setTeamId] = useState('');
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState('');
  const [joined, setJoined] = useState(null);
  const [quizInfo, setQuizInfo] = useState(null); // for team selection

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!joinCode.trim() || !name.trim()) {
      setError('Please enter both code and name');
      return;
    }
    setError('');
    setJoining(true);
    try {
      const res = await axios.post(`${API}/quiz/join`, { code: joinCode.trim(), name: name.trim(), team_id: teamId || null });
      if (res.data.team_mode && res.data.teams?.length > 0 && !teamId) {
        // Show team selection first
        setQuizInfo(res.data);
        setJoining(false);
        return;
      }
      setJoined(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to join. Check your code.');
    } finally {
      setJoining(false);
    }
  };

  const handleTeamSelect = async (tid) => {
    setTeamId(tid);
    setJoining(true);
    try {
      const res = await axios.post(`${API}/quiz/join`, { code: joinCode.trim(), name: name.trim(), team_id: tid });
      setJoined(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to join team.');
    } finally {
      setJoining(false);
    }
  };

  // If joined, show participant view
  if (joined) {
    return <QuizParticipant quizId={joined.quiz_id} participantId={joined.participant_id} participantName={name} />;
  }

  // Team selection screen
  if (quizInfo && quizInfo.team_mode && quizInfo.teams?.length > 0) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }} data-testid="team-selection">
        <div className="w-full max-w-sm">
          <div className="text-center mb-6">
            <h2 className="text-xl font-bold text-white">Pick Your Team</h2>
            <p className="text-sm text-white/60 mt-1">{quizInfo.quiz_title}</p>
          </div>
          <div className="space-y-3">
            {quizInfo.teams.map(team => (
              <button
                key={team.id}
                onClick={() => handleTeamSelect(team.id)}
                disabled={joining}
                className="w-full p-4 rounded-2xl text-left font-bold text-white transition-all hover:scale-[1.02]"
                style={{ backgroundColor: team.color, boxShadow: `0 4px 15px ${team.color}50` }}
                data-testid={`team-option-${team.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-white/30" />
                  <span>{team.name}</span>
                </div>
              </button>
            ))}
          </div>
          {joining && <div className="text-center mt-4"><Loader2 className="w-5 h-5 animate-spin mx-auto text-white" /></div>}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }} data-testid="quiz-join-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-white/15 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-4 border border-white/20">
            <Zap className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-black text-white">Join Quiz</h1>
          <p className="text-sm text-white/60 mt-1">Enter the code shared by your host</p>
        </div>

        <form onSubmit={handleJoin} className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 space-y-4 border border-white/20">
          <div>
            <label className="block text-sm font-bold text-white/80 mb-1">Join Code</label>
            <input
              type="text"
              value={joinCode}
              onChange={e => setJoinCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="w-full px-4 py-3 text-center text-2xl font-mono font-black tracking-widest bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-white/20 focus:ring-2 focus:ring-white/40 focus:border-transparent"
              maxLength={6}
              data-testid="join-code-input"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-bold text-white/80 mb-1">Your Name</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Enter your name"
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-white/30 focus:ring-2 focus:ring-white/40 focus:border-transparent"
              data-testid="join-name-input"
            />
          </div>

          {error && <p className="text-rose-300 text-sm text-center font-medium">{error}</p>}

          <button
            type="submit"
            disabled={joining || !joinCode.trim() || !name.trim()}
            className="w-full py-3 bg-white/20 hover:bg-white/30 text-white rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-30 transition-all border border-white/20"
            data-testid="join-submit-btn"
          >
            {joining ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
            {joining ? 'Joining...' : 'Join Quiz'}
          </button>
        </form>

        <p className="text-center text-xs text-white/30 mt-6">
          Powered by dsashun
        </p>
      </div>
    </div>
  );
}
